/**
 * ModalFocusContext - Provides modal focus awareness for AI Chat
 *
 * When a modal is open, the AI Chat Bubble should know:
 * - Which modal is active (EventDetail, ClientConfig, Assignment, etc.)
 * - What entity is being viewed (ID, type, key data)
 * - This becomes the PRIMARY context for AI conversations
 *
 * Usage in modals:
 * const { setModalFocus, clearModalFocus } = useModalFocus();
 *
 * useEffect(() => {
 *   if (isOpen && eventData) {
 *     setModalFocus({
 *       modalType: 'EventDetail',
 *       entityType: 'event',
 *       entityId: eventData.id,
 *       entityData: {
 *         name: eventData.name,
 *         client: eventData.client_name,
 *         date: eventData.event_date,
 *         status: eventData.status
 *       },
 *       availableActions: ['schedule', 'create_agreement', 'edit', 'cancel']
 *     });
 *   }
 *   return () => clearModalFocus();
 * }, [isOpen, eventData]);
 */

import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ModalFocusContext = createContext(null);

export const ModalFocusProvider = ({ children }) => {
  const [focusedModal, setFocusedModal] = useState(null);
  const [modalStack, setModalStack] = useState([]); // Support nested modals

  /**
   * Set the current modal focus
   * @param {Object} modalContext
   * @param {string} modalContext.modalType - Modal component name (EventDetail, ClientConfig, Assignment)
   * @param {string} modalContext.entityType - Entity type (event, client, assignment, valet)
   * @param {string} modalContext.entityId - Entity UUID
   * @param {Object} modalContext.entityData - Key entity fields for AI context
   * @param {string[]} modalContext.availableActions - Actions AI can suggest/perform
   * @param {Object} modalContext.relationships - Related entities (client, location, etc.)
   */
  const setModalFocus = useCallback((modalContext) => {
    const fullContext = {
      ...modalContext,
      openedAt: new Date().toISOString(),
      focusId: `${modalContext.modalType}-${modalContext.entityId}-${Date.now()}`
    };

    setFocusedModal(fullContext);
    setModalStack(prev => [...prev, fullContext]);

    // Publish to window for AgentChatBubble compatibility
    window.__ODV_MODAL_FOCUS__ = fullContext;

    // Also update screen context
    window.__ODV_SCREEN_CONTEXT__ = {
      ...window.__ODV_SCREEN_CONTEXT__,
      activeModal: fullContext
    };

    // Dispatch event for AgentChatBubble to pick up immediately
    window.dispatchEvent(new CustomEvent('modal-focus-change', {
      detail: { action: 'focus', modal: fullContext }
    }));

    console.log('[ModalFocus] Set focus:', modalContext.modalType, modalContext.entityId);
  }, []);

  /**
   * Clear modal focus (call on modal close)
   * @param {string} modalType - Optional, clears specific modal type
   */
  const clearModalFocus = useCallback((modalType = null) => {
    if (modalType && focusedModal?.modalType !== modalType) {
      // Different modal is focused, don't clear
      return;
    }

    const closedModal = focusedModal;

    // Pop from stack and focus previous modal if nested
    setModalStack(prev => {
      const newStack = prev.slice(0, -1);
      const previousModal = newStack[newStack.length - 1] || null;
      setFocusedModal(previousModal);

      window.__ODV_MODAL_FOCUS__ = previousModal;
      if (window.__ODV_SCREEN_CONTEXT__) {
        window.__ODV_SCREEN_CONTEXT__.activeModal = previousModal;
      }

      return newStack;
    });

    // Dispatch event
    window.dispatchEvent(new CustomEvent('modal-focus-change', {
      detail: { action: 'blur', modal: closedModal }
    }));

    console.log('[ModalFocus] Cleared focus:', closedModal?.modalType);
  }, [focusedModal]);

  /**
   * Update entity data within current modal (e.g., after save)
   */
  const updateModalData = useCallback((partialData) => {
    if (!focusedModal) return;

    const updated = {
      ...focusedModal,
      entityData: {
        ...focusedModal.entityData,
        ...partialData
      },
      updatedAt: new Date().toISOString()
    };

    setFocusedModal(updated);
    window.__ODV_MODAL_FOCUS__ = updated;
    if (window.__ODV_SCREEN_CONTEXT__) {
      window.__ODV_SCREEN_CONTEXT__.activeModal = updated;
    }
  }, [focusedModal]);

  // Generate AI-friendly context summary
  const getContextSummary = useCallback(() => {
    if (!focusedModal) return null;

    const { modalType, entityType, entityData, availableActions, relationships } = focusedModal;

    return {
      focus: `${modalType} modal is open`,
      viewing: `${entityType}: ${entityData?.name || entityData?.title || focusedModal.entityId}`,
      keyDetails: entityData,
      canDo: availableActions,
      related: relationships,
      hint: `User is looking at ${entityType} details. Prioritize questions and actions about this ${entityType}.`
    };
  }, [focusedModal]);

  return (
    <ModalFocusContext.Provider value={{
      focusedModal,
      modalStack,
      setModalFocus,
      clearModalFocus,
      updateModalData,
      getContextSummary,
      hasFocus: !!focusedModal
    }}>
      {children}
    </ModalFocusContext.Provider>
  );
};

export const useModalFocus = () => {
  const context = useContext(ModalFocusContext);
  if (!context) {
    // Return safe no-op functions if used outside provider
    return {
      focusedModal: null,
      modalStack: [],
      setModalFocus: () => console.warn('[ModalFocus] Used outside ModalFocusProvider'),
      clearModalFocus: () => {},
      updateModalData: () => {},
      getContextSummary: () => null,
      hasFocus: false
    };
  }
  return context;
};

/**
 * Hook for modals to easily manage their focus lifecycle
 *
 * Usage:
 * useModalFocusEffect({
 *   isOpen,
 *   modalType: 'EventDetail',
 *   entityType: 'event',
 *   entityId: event?.id,
 *   entityData: event ? {
 *     name: event.name,
 *     client: event.client_name,
 *     date: event.event_date,
 *     status: event.status
 *   } : null,
 *   availableActions: ['schedule', 'create_agreement', 'edit']
 * });
 */
export const useModalFocusEffect = ({
  isOpen,
  modalType,
  entityType,
  entityId,
  entityData,
  availableActions = [],
  relationships = {}
}) => {
  const { setModalFocus, clearModalFocus } = useModalFocus();

  useEffect(() => {
    if (isOpen && entityId) {
      setModalFocus({
        modalType,
        entityType,
        entityId,
        entityData,
        availableActions,
        relationships
      });
    }

    return () => {
      if (isOpen) {
        clearModalFocus(modalType);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityId, JSON.stringify(entityData)]);
};

export default ModalFocusContext;
